import databaseService from './database.service';

/**
 * Item Service for ZyloBill Web App
 * Adapted from desktop app to work with browser PouchDB
 */

export const itemService = {
  /**
   * List all items
   */
  async list() {
    const items = await databaseService.getItems();
    
    // Sort by name
    items.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return items.map(item => ({
      ...item,
      id: item._id,
      // Map database item_type to UI type
      type: item.item_type === 'goods' ? 'product' : 
            item.item_type === 'services' ? 'service' : 
            item.type || 'product',
    }));
  },

  /**
   * Get item picker list (for dropdowns)
   */
  async picker(params = {}) {
    const items = await this.list();
    
    let filtered = items;

    // Filter by type if provided
    if (params.type) {
      filtered = filtered.filter(item => item.type === params.type);
    }

    // Filter by search term if provided
    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name || '').toLowerCase().includes(search) ||
        (item.description || '').toLowerCase().includes(search) ||
        (item.hsn || '').toLowerCase().includes(search) ||
        (item.sac || '').toLowerCase().includes(search)
      );
    }

    return filtered;
  },

  /**
   * Add new item
   */
  async add(data) {
    const itemId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Map form 'type' to database 'item_type'
    const itemType = data.type === 'product' ? 'goods' : 'services';
    
    const item = {
      _id: itemId,
      type: 'item',
      name: data.name || '',
      description: data.description || '',
      item_type: itemType,
      rate: data.price || data.rate || 0,
      price: data.price || data.rate || 0,
      gst_rate: data.tax || data.gst_rate || 0,
      tax: data.tax || data.gst_rate || 0,
      tax_rate: data.tax || data.gst_rate || 0,
      gst_type: data.gst_type || 'cgst_sgst',
      hsn: data.hsn || '',
      sac: data.sac || '',
      uom: data.uom || 'unit',
      billingFrequency: data.billingFrequency || null,
      notes: data.notes || '',
      deleted: false,
    };

    const result = await databaseService.createDocument(item);
    
    return {
      ...item,
      id: result.id,
    };
  },

  /**
   * Update item
   */
  async update(id, data) {
    const currentItem = await databaseService.readDocument(id);
    
    if (!currentItem) {
      throw new Error('Item not found');
    }

    // Map form 'type' to database 'item_type'
    const itemType = data.type === 'product' ? 'goods' : data.type === 'service' ? 'services' : undefined;

    const updatedItem = {
      ...currentItem,
      name: data.name !== undefined ? data.name : currentItem.name,
      description: data.description !== undefined ? data.description : currentItem.description,
      item_type: itemType !== undefined ? itemType : currentItem.item_type,
      rate: data.price !== undefined ? data.price : (data.rate !== undefined ? data.rate : currentItem.rate),
      price: data.price !== undefined ? data.price : (data.rate !== undefined ? data.rate : currentItem.price),
      gst_rate: data.tax !== undefined ? data.tax : (data.gst_rate !== undefined ? data.gst_rate : currentItem.gst_rate),
      tax: data.tax !== undefined ? data.tax : (data.gst_rate !== undefined ? data.gst_rate : currentItem.tax),
      tax_rate: data.tax !== undefined ? data.tax : (data.gst_rate !== undefined ? data.gst_rate : currentItem.tax_rate),
      gst_type: data.gst_type !== undefined ? data.gst_type : currentItem.gst_type,
      hsn: data.hsn !== undefined ? data.hsn : currentItem.hsn,
      sac: data.sac !== undefined ? data.sac : currentItem.sac,
      uom: data.uom !== undefined ? data.uom : currentItem.uom,
      billingFrequency: data.billingFrequency !== undefined ? data.billingFrequency : currentItem.billingFrequency,
      notes: data.notes !== undefined ? data.notes : currentItem.notes,
    };

    await databaseService.updateDocument(updatedItem);
    
    return {
      ...updatedItem,
      id: updatedItem._id,
    };
  },

  /**
   * Delete item (soft delete)
   */
  async delete(id) {
    await databaseService.deleteDocument(id);
    return { success: true };
  },

  /**
   * Delete all archived items
   */
  async deleteAllArchived() {
    const items = await databaseService.getItems();
    const archivedItems = items.filter(item => item.archived_at);
    
    for (const item of archivedItems) {
      await databaseService.deleteDocument(item._id);
    }
    
    return { 
      success: true,
      count: archivedItems.length 
    };
  },

  /**
   * Archive item
   */
  async archive(id) {
    const currentItem = await databaseService.readDocument(id);
    
    if (!currentItem) {
      throw new Error('Item not found');
    }

    const archivedItem = {
      ...currentItem,
      archived_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(archivedItem);
    
    return {
      ...archivedItem,
      id: archivedItem._id,
    };
  },

  /**
   * Restore archived item
   */
  async restore(id) {
    const currentItem = await databaseService.readDocument(id);
    
    if (!currentItem) {
      throw new Error('Item not found');
    }

    const restoredItem = {
      ...currentItem,
      archived_at: null,
    };

    await databaseService.updateDocument(restoredItem);
    
    return {
      ...restoredItem,
      id: restoredItem._id,
    };
  },

  /**
   * Get item by ID
   */
  async get(id) {
    const item = await databaseService.readDocument(id);
    
    if (!item) {
      return null;
    }

    return {
      ...item,
      id: item._id,
    };
  },
};
